// frontend/src/pages/Vacancies/index.tsx

import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { VacancyCard } from '@/components/vacancy/VacancyCard'
import { VacancyFilters } from '@/components/vacancy/VacancyFilters'
import { SearchBar } from '@/components/search/SearchBar'
import { Button } from '@/components/common/UI/Button'
import { Spinner } from '@/components/common/UI/Spinner'
import { useAuthStore } from '@/store'

interface SavedVacancySearch {
  id: string
  name: string
  query: string
  filters: Record<string, unknown>
}

const SAVED_SEARCHES_KEY = 'lenjob:saved-vacancy-searches'

const VacancyAlertSubscriptions: React.FC<{ query: string; filters: any }> = ({ query, filters }) => {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const subscriptions = useQuery({ queryKey: ['subscriptions'], queryFn: () => apiClient.get<any[]>('/v1/subscriptions') })
  const create = useMutation({ mutationFn: () => apiClient.post('/v1/subscriptions', {
    title: title.trim() || query.trim() || filters.city || 'Мой поиск вакансий',
    filters: Object.fromEntries(Object.entries({ query: query.trim(), city: filters.city, experience_level: filters.experience_level, salary_from: filters.salary_from, remote: filters.remote }).filter(([, value]) => value !== '' && value !== null && value !== undefined)),
  }), onSuccess: () => { setTitle(''); queryClient.invalidateQueries({ queryKey: ['subscriptions'] }) } })
  const update = useMutation({ mutationFn: ({ id, is_active }: any) => apiClient.patch(`/v1/subscriptions/${id}`, { is_active }), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscriptions'] }) })
  const remove = useMutation({ mutationFn: (id: number) => apiClient.delete(`/v1/subscriptions/${id}`), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscriptions'] }) })
  return <section className="mb-5 rounded-xl border border-blue-100 bg-blue-50/60 p-4"><div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="font-semibold text-secondary-900">Получать новые вакансии по email</h2><p className="mt-1 text-xs text-secondary-600">Подборка приходит примерно раз в 15 минут. Можно отключить подписку в любой момент.</p></div><form className="flex gap-2" onSubmit={event => { event.preventDefault(); create.mutate() }}><input value={title} onChange={event => setTitle(event.target.value)} maxLength={120} placeholder="Название подписки" className="w-40 rounded-lg border border-secondary-300 px-3 py-2 text-sm sm:w-52"/><Button size="sm" type="submit" disabled={create.isPending}>Подписаться</Button></form></div>{create.isError && <p className="mt-2 text-sm text-error">Не удалось создать подписку. Проверьте вход в аккаунт.</p>}{Array.isArray(subscriptions.data) && subscriptions.data.length > 0 && <ul className="mt-3 divide-y divide-blue-100">{subscriptions.data.map((item: any) => <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"><span>{item.title} · {item.is_active ? 'включена' : 'на паузе'}</span><span className="flex gap-3"><button className="text-primary-700 underline" onClick={() => update.mutate({ id: item.id, is_active: !item.is_active })}>{item.is_active ? 'Пауза' : 'Включить'}</button><button className="text-secondary-600 underline" onClick={() => remove.mutate(item.id)}>Удалить</button></span></li>)}</ul>}</section>
}

const Vacancies: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const [filters, setFilters] = useState<any>(() => {
    const city = new URLSearchParams(window.location.search).get('city')?.trim()
    return city ? { city } : {}
  })
  const [page, setPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const [searchName, setSearchName] = useState('')
  const [selectedSavedSearchId, setSelectedSavedSearchId] = useState('')
  const [savedSearches, setSavedSearches] = useState<SavedVacancySearch[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(SAVED_SEARCHES_KEY) || '[]')
    } catch {
      return []
    }
  })

  const query = searchParams.get('q') || ''
  const cityLanding = searchParams.get('city')?.trim() || ''

  useEffect(() => {
    setFilters((current: any) => current.city === cityLanding ? current : { ...current, city: cityLanding })
    setPage(1)
  }, [cityLanding])

  // Получение вакансий
  const { data, isLoading, error } = useQuery({
    queryKey: ['vacancies', query, filters, page],
    // Keep the current results mounted while filters (especially the city
    // autocomplete) trigger a new request. Otherwise the full-page spinner
    // unmounts the input on every keystroke and drops focus.
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const params: Record<string, unknown> = {
        page,
        size: 20,
        search: query || undefined,
        ...filters
      }
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key]
      })
      const response = await apiClient.get<any>('/v1/vacancies', { params })
      // API currently returns a page as an array; normalize it for the UI.
      return Array.isArray(response) ? { items: response, total: response.length } : response
    }
  })

  const handleSearch = (searchQuery: string) => {
    setSearchParams({ q: searchQuery })
    setPage(1)
  }

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters)
    if (cityLanding && !newFilters.city) {
      setSearchParams((current) => {
        current.delete('city')
        return current
      }, { replace: true })
    }
    setPage(1)
  }

  const handleLoadMore = () => {
    setPage(page + 1)
  }

  const persistSavedSearches = (next: SavedVacancySearch[]) => {
    setSavedSearches(next)
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(next))
  }

  const saveCurrentSearch = () => {
    const name = searchName.trim()
    if (!name) return
    const next = [...savedSearches, {
      id: `${Date.now()}`,
      name,
      query,
      filters: { ...filters },
    }]
    persistSavedSearches(next)
    setSearchName('')
  }

  const applySavedSearch = (id: string) => {
    setSelectedSavedSearchId(id)
    const saved = savedSearches.find((item) => item.id === id)
    if (!saved) return
    setSearchParams(saved.query ? { q: saved.query } : {})
    setFilters(saved.filters)
    setPage(1)
    setShowFilters(true)
  }

  const deleteSelectedSearch = () => {
    if (!selectedSavedSearchId) return
    persistSavedSearches(savedSearches.filter((item) => item.id !== selectedSavedSearchId))
    setSelectedSavedSearchId('')
  }

  if (isLoading && page === 1) {
    return (
      <div className="container-custom py-12 flex justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="container-custom py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{cityLanding ? `Работа и вакансии в ${cityLanding}` : 'Работа и актуальные вакансии'}</h1>
        {isAuthenticated && user?.role === 'employer' && (
          <Button onClick={() => window.location.assign('/vacancies/create')}>
            Создать вакансию
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="mb-6">
        <SearchBar
          placeholder="Поиск вакансий, компаний, навыков..."
          onSearch={handleSearch}
          initialValue={query}
        />
      </div>

      {/* Filters toggle */}
      <button
        className="text-primary-600 hover:text-primary-700 text-sm font-medium mb-4"
        onClick={() => setShowFilters(!showFilters)}
      >
        {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
      </button>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6">
          <VacancyFilters key={cityLanding || 'all-cities'} initialFilters={filters} onFilterChange={handleFilterChange} />
        </div>
      )}

      {/* Save and restore candidate searches */}
      <div className="mb-4 flex flex-col gap-3 rounded-xl border border-secondary-200 bg-white p-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <label htmlFor="save-search-name" className="mb-1 block text-sm font-medium text-secondary-700">
            Сохранить текущий поиск
          </label>
          <div className="flex gap-2">
            <input
              id="save-search-name"
              value={searchName}
              onChange={(event) => setSearchName(event.target.value)}
              placeholder="Например, удалённая работа"
              maxLength={60}
              className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm sm:w-64"
            />
            <Button type="button" size="sm" variant="outline" onClick={saveCurrentSearch} disabled={!searchName.trim()}>
              Сохранить
            </Button>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label htmlFor="saved-searches" className="mb-1 block text-sm font-medium text-secondary-700">
              Мои поиски на этом устройстве
            </label>
            <select
              id="saved-searches"
              value={selectedSavedSearchId}
              onChange={(event) => applySavedSearch(event.target.value)}
              className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm sm:w-64"
            >
              <option value="">Выберите сохранённый поиск</option>
              {savedSearches.map((saved) => <option key={saved.id} value={saved.id}>{saved.name}</option>)}
            </select>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={deleteSelectedSearch}
            disabled={!selectedSavedSearchId}
            aria-label="Удалить сохранённый поиск"
          >
            Удалить
          </Button>
        </div>
      </div>
      {isAuthenticated && user?.role === 'candidate' && <VacancyAlertSubscriptions query={query} filters={filters} />}

      <p className="text-secondary-600 text-sm mb-4">
        Найдено: {data?.total || 0} вакансий
      </p>

      {/* Results */}
      {error ? (
        <div className="text-center py-8">
          <p className="text-error">Ошибка загрузки вакансий</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {data?.items?.map((vacancy: any) => (
              <VacancyCard key={vacancy.id} vacancy={vacancy} onApply={() => navigate(`/vacancies/${vacancy.id}`)} />
            ))}
          </div>

          {data?.items?.length === 0 && (
            <div className="text-center py-12">
              <p className="text-secondary-500">Вакансий не найдено</p>
            </div>
          )}

          {/* Load more */}
          {data && data.total > data.items?.length && (
            <div className="text-center mt-6">
              <Button variant="outline" onClick={handleLoadMore} isLoading={isLoading}>
                Загрузить еще
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Vacancies
